import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, Star } from "lucide-react";
import { loc } from "@/lib/api";

const STATUS_STYLES = {
  etude: "bg-slate-100 text-slate-700",
  permis: "bg-amber-100 text-amber-800",
  construction: "bg-sky-100 text-sky-800",
  commercialisation: "bg-emerald-100 text-emerald-800",
  livre: "bg-[#111827] text-white",
};

export const StatusBadge = ({ status, className = "" }) => {
  const { t } = useTranslation();
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLES[status] || "bg-slate-100 text-slate-700"} ${className}`}
    >
      {t(`proj.status.${status}`)}
    </span>
  );
};

export const ProgressBar = ({ value, light = false }) => {
  const { t } = useTranslation();
  return (
    <div>
      <div className={`flex items-center justify-between text-[11px] font-medium ${light ? "text-white/60" : "text-slate-500"}`}>
        <span>{t("proj.progress")}</span>
        <span>{value}%</span>
      </div>
      <div className={`mt-1.5 h-1 w-full overflow-hidden rounded-full ${light ? "bg-white/15" : "bg-[#111827]/10"}`}>
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${value}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="h-full rounded-full bg-[#2E8BFF]"
        />
      </div>
    </div>
  );
};

export const ProjectCard = ({ project, flagship = false, wide = false }) => {
  const { t, i18n } = useTranslation();
  return (
    <Link
      to={`/projets/${project.slug}`}
      data-testid={`project-card-${project.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#111827]/8 bg-white transition-shadow duration-500 hover:shadow-[0_20px_60px_rgba(17,24,39,0.12)]"
    >
      <div className={`relative overflow-hidden ${wide ? "h-72 md:h-96" : "h-56"}`}>
        <img
          src={project.images.cover}
          alt={project.title}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute left-4 top-4 flex gap-2">
          <StatusBadge status={project.status} />
          {flagship && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#2E8BFF] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white" data-testid="flagship-badge">
              <Star className="h-3 w-3 fill-current" />
              {t("proj.flagship")}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-slate-500">
          <MapPin className="h-3.5 w-3.5 text-[#2E8BFF]" />
          {project.location.name} · {loc(project.type, i18n.language)}
        </p>
        <h3 className="mt-2 font-display text-2xl text-[#111827]">{project.title}</h3>
        <div className="mt-auto pt-5">
          <ProgressBar value={project.progress} />
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2E8BFF]">
            {t("proj.view")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </Link>
  );
};
